import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar.jsx';
import { api } from '../api/client.js';

export default function AccessManagement() {
  const { appId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/api/access/apps/${appId}`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (loading && !data) {
    return (
      <>
        <Navbar />
        <main className="max-w-6xl mx-auto px-6 py-8">Loading…</main>
      </>
    );
  }

  if (error && !data) {
    return (
      <>
        <Navbar />
        <main className="max-w-6xl mx-auto px-6 py-8">
          <div className="text-red-700 bg-red-50 border border-red-200 rounded-lg p-4">
            {error}
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-1">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center font-medium text-sm"
            style={{
              backgroundColor: '#FAECE7',
              color: '#712B13'
            }}
          >
            {data.app.iconLabel}
          </div>
          <div>
            <h1 className="text-xl font-medium leading-tight">
              {data.app.name} · access management
            </h1>
            <p className="text-xs text-muted">Who can launch this app on the VF AI Launchpad</p>
          </div>
          <Link
            to="/"
            className="ml-auto text-sm text-muted hover:text-ink"
          >
            ← Back to dashboard
          </Link>
        </div>

        {error && (
          <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
            {error}
          </div>
        )}

        {/* Two-column: direct users + groups */}
        <div className="grid md:grid-cols-2 gap-3 mt-6">
          <DirectUsersSection
            grants={data.directUsers}
            appId={appId}
            onChange={refresh}
            setError={setError}
          />
          <GroupsSection
            grants={data.groups}
            appId={appId}
            onChange={refresh}
            setError={setError}
          />
        </div>

        {/* OUs full width */}
        <div className="mt-3">
          <OUsSection
            grants={data.ous}
            appId={appId}
            onChange={refresh}
            setError={setError}
          />
        </div>

        {/* Summary footer */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-border text-sm">
          <span>
            <strong>{data.uniqueUsers} unique users</strong>
            <span className="text-muted ml-1">have access via these grants</span>
          </span>
          <span className="text-muted text-xs">
            Last Directory sync:{' '}
            {data.lastDirectorySync
              ? new Date(data.lastDirectorySync).toLocaleString()
              : 'never'}
          </span>
        </div>

        {/* Roles legend */}
        <div className="mt-6 bg-neutral-50 rounded-xl p-4">
          <p className="text-sm font-medium mb-2">Roles in this Launchpad</p>
          <div className="flex items-start gap-2 mb-2">
            <Pill kind="admin">admin</Pill>
            <span className="text-xs text-muted">
              Manages apps, configures access policies, sees usage across all apps
            </span>
          </div>
          <div className="flex items-start gap-2">
            <Pill kind="user">user</Pill>
            <span className="text-xs text-muted">
              Launches apps they're granted (via direct, group, or OU), sees own usage
            </span>
          </div>
          <p className="text-xs text-muted mt-3">
            Note: When a user's Google account is disabled, all access — direct, group, OU
            — is revoked instantly via the next Directory sync.
          </p>
        </div>
      </main>
    </>
  );
}

// ---------- Sections ----------

function DirectUsersSection({ grants, appId, onChange, setError }) {
  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post(`/api/access/apps/${appId}/grants`, { type: 'user', email });
      setEmail('');
      setAdding(false);
      onChange();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (grantId) => {
    try {
      await api.delete(`/api/access/apps/${appId}/grants/${grantId}`);
      onChange();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  return (
    <Section title="Direct user grants" onAdd={() => setAdding(true)} addLabel="Add user">
      {adding && (
        <form onSubmit={submit} className="flex gap-2 px-3 py-2 border-b border-border">
          <input
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@verifiedfirst.com"
            className="flex-1 text-sm border border-border rounded-md px-2 py-1"
          />
          <button
            type="submit"
            disabled={busy}
            className="text-sm bg-ink text-white rounded-md px-3 py-1 disabled:opacity-50"
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => setAdding(false)}
            className="text-sm text-muted px-2"
          >
            Cancel
          </button>
        </form>
      )}
      {grants.length === 0 && (
        <EmptyRow text="No direct user grants yet." />
      )}
      {grants.map((g) => (
        <Row key={g.grantId}>
          <div className="flex items-center gap-2 flex-1">
            <Pill kind="user">user</Pill>
            <span className="text-sm">{g.email}</span>
            {g.name && <span className="text-xs text-muted">· {g.name}</span>}
          </div>
          <RemoveButton onClick={() => remove(g.grantId)} />
        </Row>
      ))}
    </Section>
  );
}

function GroupsSection({ grants, appId, onChange, setError }) {
  const [picking, setPicking] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!picking) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/api/directory/groups?q=${encodeURIComponent(search)}`);
        setResults(r.data.groups);
      } catch {
        // ignore
      }
    }, 200);
    return () => clearTimeout(t);
  }, [search, picking]);

  const grant = async (groupId) => {
    try {
      await api.post(`/api/access/apps/${appId}/grants`, { type: 'group', groupId });
      setPicking(false);
      setSearch('');
      onChange();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const remove = async (grantId) => {
    try {
      await api.delete(`/api/access/apps/${appId}/grants/${grantId}`);
      onChange();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  return (
    <Section title="Google groups" onAdd={() => setPicking(true)} addLabel="Add group">
      {picking && (
        <div className="border-b border-border">
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search groups…"
            className="w-full text-sm border-0 px-3 py-2 focus:outline-none"
          />
          <div className="max-h-48 overflow-y-auto">
            {results.map((r) => (
              <button
                key={r.id}
                onClick={() => grant(r.id)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-neutral-50 flex justify-between"
              >
                <span>
                  <strong>{r.name}</strong>{' '}
                  <span className="text-xs text-muted">{r.email}</span>
                </span>
                <span className="text-xs text-muted">{r.memberCount} members</span>
              </button>
            ))}
            {results.length === 0 && (
              <div className="px-3 py-2 text-xs text-muted">No groups match.</div>
            )}
          </div>
          <button
            onClick={() => setPicking(false)}
            className="text-xs text-muted px-3 py-1 border-t border-border w-full text-left hover:bg-neutral-50"
          >
            Cancel
          </button>
        </div>
      )}
      {grants.length === 0 && <EmptyRow text="No group grants yet." />}
      {grants.map((g) => (
        <Row key={g.grantId}>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Pill kind="group">group</Pill>
            <span className="text-sm truncate">{g.email}</span>
          </div>
          <span className="text-xs text-muted whitespace-nowrap">
            {g.memberCount} members
          </span>
          <RemoveButton onClick={() => remove(g.grantId)} />
        </Row>
      ))}
    </Section>
  );
}

function OUsSection({ grants, appId, onChange, setError }) {
  const [picking, setPicking] = useState(false);
  const [ous, setOus] = useState([]);

  useEffect(() => {
    if (!picking) return;
    api
      .get('/api/directory/ous')
      .then((r) => setOus(r.data.ous))
      .catch(() => {});
  }, [picking]);

  const grant = async (ouPath) => {
    try {
      await api.post(`/api/access/apps/${appId}/grants`, { type: 'ou', ouPath });
      setPicking(false);
      onChange();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const remove = async (grantId) => {
    try {
      await api.delete(`/api/access/apps/${appId}/grants/${grantId}`);
      onChange();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  return (
    <Section title="Organizational units" onAdd={() => setPicking(true)} addLabel="Add OU">
      {picking && (
        <div className="border-b border-border max-h-64 overflow-y-auto">
          {ous.map((ou) => (
            <button
              key={ou.id}
              onClick={() => grant(ou.path)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-neutral-50 flex justify-between"
            >
              <span>
                <Pill kind="ou">OU</Pill> <span className="ml-2">{ou.path}</span>
              </span>
              <span className="text-xs text-muted">{ou.memberCount} members</span>
            </button>
          ))}
          <button
            onClick={() => setPicking(false)}
            className="text-xs text-muted px-3 py-1 border-t border-border w-full text-left hover:bg-neutral-50"
          >
            Cancel
          </button>
        </div>
      )}
      {grants.length === 0 && <EmptyRow text="No OU grants yet." />}
      {grants.map((g) => (
        <Row key={g.grantId}>
          <div className="flex items-center gap-2 flex-1">
            <Pill kind="ou">OU</Pill>
            <span className="text-sm">{g.path}</span>
            {g.path !== '/' && (
              <span className="text-xs text-muted">includes child OUs</span>
            )}
          </div>
          <span className="text-xs text-muted whitespace-nowrap">
            {g.memberCount} members
          </span>
          <RemoveButton onClick={() => remove(g.grantId)} />
        </Row>
      ))}
    </Section>
  );
}

// ---------- Building blocks ----------

function Section({ title, onAdd, addLabel, children }) {
  return (
    <div className="bg-white border border-border rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-neutral-50 border-b border-border">
        <h2 className="text-sm font-medium">{title}</h2>
        {onAdd && (
          <button
            onClick={onAdd}
            className="text-xs border border-border rounded-md px-2.5 py-1 hover:bg-white"
          >
            + {addLabel}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function Row({ children }) {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border last:border-b-0">
      {children}
    </div>
  );
}

function EmptyRow({ text }) {
  return <div className="px-4 py-3 text-xs text-muted italic">{text}</div>;
}

function RemoveButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="text-xs text-muted hover:text-red-600 px-2"
    >
      remove
    </button>
  );
}

function Pill({ kind, children }) {
  const styles = {
    user:  { background: '#E6F1FB', color: '#185FA5' },
    group: { background: '#E1F5EE', color: '#085041' },
    ou:    { background: '#FAEEDA', color: '#633806' },
    admin: { background: '#FBEAF0', color: '#72243E' }
  };
  return (
    <span
      className="inline-block text-xs font-medium px-2 py-0.5 rounded-full"
      style={styles[kind] || {}}
    >
      {children}
    </span>
  );
}
